import { Fragment, type ReactNode } from "react";

import { useTranslation } from "next-i18next";

import type { ProductUnknown } from "@/common/contracts/product.contracts";
import { cn } from "@/common/utils/css.utils";
import { BoostedLabel } from "@/features/boost-ad/components/BoostedLabel";
import { CuratedLabel } from "@/features/curation/components/CuratedLabel";
import { PromotionPaidLabel } from "@/features/promotions/components/PromotionPaidLabel";
import { ShowroomModelLabel } from "@/features/showroom-item/components/ShowroomModelLabel";

type ProductLabelKind = "paid" | "curated" | "boosted" | "showroom";

type ProductLabelListProps = {
  curated: boolean;
  boosted: boolean;
  showroom: boolean;
  /** WHO-5947 F3: grey "Reserved" chip. A status, not a label: never counted against `max`. */
  reserved?: boolean;
  paidLabel?: string | null;
  /**
   * Show at most this many labels, highest priority first. Product cards pass 2; the PDP passes
   * nothing and shows them all.
   */
  max?: number;
  /**
   * Never let `max` drop "Boosted". Set where the label is the card's only paid-ranking marker
   * (see `ProductCardLabels`).
   */
  keepBoosted?: boolean;
  className?: string;
};

type ProductLabel = { kind: ProductLabelKind; node: ReactNode };

/**
 * The labels that fit in `max`, still in priority order. A kept "Boosted" takes its place first,
 * and the rest fill what is left from the top of the priority list.
 */
const capLabels = (
  labels: ProductLabel[],
  max: number | undefined,
  keepBoosted: boolean
) => {
  if (max === undefined || labels.length <= max) {
    return labels;
  }

  const isPinned = (label: ProductLabel) =>
    keepBoosted && label.kind === "boosted";
  const pinned = labels.filter(isPinned).slice(0, max);
  const others = labels
    .filter(label => !isPinned(label))
    .slice(0, Math.max(0, max - pinned.length));

  return labels.filter(
    label => pinned.includes(label) || others.includes(label)
  );
};

/**
 * The Must go / Boosted / Curated / Showroom row, shared by the product card and the PDP, in that
 * priority order (Dieke, 2026-10-28: Boosted before Curated). Callers work out the flags, since the search DTO and the
 * gateway product carry them under different fields.
 */
export const ProductLabelList = ({
  curated,
  boosted,
  showroom,
  reserved = false,
  paidLabel,
  max,
  keepBoosted = false,
  className,
}: ProductLabelListProps) => {
  const { t } = useTranslation();

  /* Nothing to show — skip the wrapper rather than leave its negative margins in the layout. */
  if (!curated && !boosted && !showroom && !reserved && !paidLabel) {
    return null;
  }

  /* BoostedLabel and CuratedLabel are typed against the gateway product and re-check the flag. */
  const product: ProductUnknown = { isBoosted: boosted, isCurated: curated };

  const labels: ProductLabel[] = [];

  if (paidLabel) {
    labels.push({
      kind: "paid",
      node: <PromotionPaidLabel text={paidLabel} />,
    });
  }

  if (boosted) {
    labels.push({ kind: "boosted", node: <BoostedLabel product={product} /> });
  }

  if (curated) {
    labels.push({ kind: "curated", node: <CuratedLabel product={product} /> });
  }

  if (showroom) {
    labels.push({ kind: "showroom", node: <ShowroomModelLabel /> });
  }

  const visible = capLabels(labels, max, keepBoosted);

  return (
    <div
      className={cn(
        "-ml-2 -mt-2 flex flex-wrap space-x-2 space-y-2 [&>:first-child]:ml-2 [&>:first-child]:mt-2",
        className
      )}
    >
      {visible.map(label => (
        <Fragment key={label.kind}>{label.node}</Fragment>
      ))}
      {reserved && (
        <span
          className="inline-flex items-center rounded-full bg-grey-200 px-2 py-0.5 font-title text-sm leading-6 text-grey-800"
          data-testid="product-label-reserved"
        >
          {t("common:reservation-reserved-label", "Reserved")}
        </span>
      )}
    </div>
  );
};
